import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DeleteDepartmentUserRequest,
  DepartmentResponse,
  DepartmentUserResponse,
  UpdateDepartmentRequest,
  UpdateDepartmentUserRequest,
} from '@pixacare/dtos';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { refreshClientUserDepartments$ } from './client-user.http.service';
import { HTTP_CACHE_MAX_AGE } from './http-cache-max-age';

@Injectable({
  providedIn: 'root',
})
export class DepartmentHttpService {
  private readonly http = inject(HttpClient);

  getDepartmentUsers(clientCode: string, departmentId: number): Observable<DepartmentUserResponse[]> {
    return this.http.get<DepartmentUserResponse[]>(
      `${environment.apiBaseUrl}/department/${departmentId}/users?cc=${clientCode}`,
    );
  }

  @Cacheable({
    maxAge: HTTP_CACHE_MAX_AGE,
    cacheBusterObserver: refreshClientUserDepartments$,
  })
  getDepartments(clientCode: string): Observable<DepartmentResponse[]> {
    return this.http.get<DepartmentResponse[]>(`${environment.apiBaseUrl}/department/?cc=${clientCode}`, {
      headers: { 'X-version': '2.0' },
    });
  }

  updateDepartment({
    clientCode,
    departmentId,
    name,
    isOpen,
    medicalSpecialtyId,
  }: UpdateDepartmentRequest): Observable<DepartmentResponse> {
    return this.http.patch<DepartmentResponse>(`${environment.apiBaseUrl}/department/${departmentId}`, {
      clientCode,
      name,
      isOpen,
      medicalSpecialtyId,
    });
  }

  @CacheBuster({
    cacheBusterNotifier: refreshClientUserDepartments$,
  })
  create(clientCode: string, name: string): Observable<DepartmentResponse> {
    return this.http.post<DepartmentResponse>(`${environment.apiBaseUrl}/department/?cc=${clientCode}`, { name });
  }

  updateUser({
    clientCode,
    departmentId,
    departmentUserId,
    isAdmin,
    disabledOn,
  }: UpdateDepartmentUserRequest): Observable<UpdateDepartmentUserRequest> {
    return this.http.patch<UpdateDepartmentUserRequest>(
      `${environment.apiBaseUrl}/department/${departmentId}/user/${departmentUserId}`,
      { isAdmin, disabledOn: disabledOn?.toISOString() },
      {
        params: {
          cc: clientCode,
        },
      },
    );
  }

  @CacheBuster({
    cacheBusterNotifier: refreshClientUserDepartments$,
  })
  removeUser({
    userId,
    clientCode,
    departmentId,
  }: DeleteDepartmentUserRequest): Observable<HttpResponse<DeleteDepartmentUserRequest>> {
    return this.http.delete<DeleteDepartmentUserRequest>(
      `${environment.apiBaseUrl}/department/${departmentId}/user/${userId}?cc=${clientCode}`,
      { observe: 'response' },
    );
  }
}
